import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  extractUnmaskedIdFromResponse,
  unmaskMaskedId,
} from "model/MetaModel";

const unmaskCache = new Map();

const COPY_TOAST_STYLE = {
  className: "toast-text-wrap",
  autoClose: 2000,
  style: {
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    maxWidth: "min(28rem, 92vw)",
  },
  bodyStyle: {
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
};

const MaskedIdTableCell = ({ maskedId, className = "" }) => {
  const displayId = String(maskedId ?? "").trim();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actualId, setActualId] = useState("");
  const wrapperRef = useRef(null);

  const closeTooltip = useCallback(() => {
    setOpen(false);
    setLoading(false);
  }, []);

  const copyIdToClipboard = useCallback(async (idValue) => {
    const textToCopy = String(idValue ?? "").trim();
    if (!textToCopy) return;

    try {
      await navigator.clipboard.writeText(textToCopy);
      toast.success(`Copied ${textToCopy}`, COPY_TOAST_STYLE);
    } catch {
      toast.error("Unable to copy ID");
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        closeTooltip();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open, closeTooltip]);

  const handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!displayId) return;

    if (open) {
      closeTooltip();
      return;
    }

    setOpen(true);

    if (unmaskCache.has(displayId)) {
      const cachedId = unmaskCache.get(displayId);
      setActualId(cachedId);
      void copyIdToClipboard(cachedId);
      return;
    }

    setLoading(true);
    const response = await unmaskMaskedId(displayId);
    const resolvedId = extractUnmaskedIdFromResponse(response) || displayId;
    unmaskCache.set(displayId, resolvedId);
    setActualId(resolvedId);
    setLoading(false);
    void copyIdToClipboard(resolvedId);
  };

  if (!displayId) {
    return <span className="text-muted">—</span>;
  }

  return (
    <span
      ref={wrapperRef}
      className={["masked-id-table-cell", className].filter(Boolean).join(" ")}
    >
      <button
        type="button"
        className="masked-id-table-cell__trigger"
        onClick={handleClick}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {displayId}
      </button>
      {open ? (
        <span className="masked-id-unmask-tooltip" role="tooltip">
          {loading ? "Loading..." : actualId || "—"}
        </span>
      ) : null}
    </span>
  );
};

export default MaskedIdTableCell;
